/**
 * Submission Settings — `/settings/submissions`
 *
 * Firm-wide presenter / agent credentials for filing on behalf of
 * clients with HMRC (SDLT), HMLR (AP1), Companies House (MR01) and
 * Thirdfort (AML).
 *
 * The big one is Companies House — when you file an MR01 on behalf of
 * a third-party company you need:
 *   - Presenter ID (the firm's WebFiling account number)
 *   - Presenter Authentication Code (the firm's account password)
 *   - The CHARGOR company's per-company Authentication Code (6-char)
 *
 * Secrets are masked on the way down (last 4 chars visible) so a
 * solicitor browsing this page never sees the full password again
 * after setting it. Sending the masked value back is a no-op.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import API, { formatApiErrorDetail } from "@/lib/api";
import Header from "@/components/app/Header";
import TitleRiskBadge from "@/components/app/TitleRiskBadge";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Buildings, Stamp, Bank, ShieldCheck, CheckCircle, XCircle, Plus, Trash, FloppyDisk, Pulse, Warning, Link as LinkIcon, ArrowsClockwise, PlugsConnected,
} from "@phosphor-icons/react";

function StatusBadge({ configured }) {
  if (configured) {
    return (
      <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
        <CheckCircle weight="fill" className="w-3 h-3" /> Configured
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
      <XCircle weight="fill" className="w-3 h-3" /> Not set
    </span>
  );
}

function Field({ label, hint, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 text-sm font-mono"
        autoComplete="off"
        data-testid={testid}
      />
      {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
    </div>
  );
}

function SectionCard({ icon: Icon, title, subtitle, configured, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-lg shadow-sm">
      <header className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-md bg-[#1462a0]/10 text-[#1462a0] flex items-center justify-center">
            <Icon weight="duotone" className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>{title}</h2>
            <p className="text-xs text-slate-500">{subtitle}</p>
          </div>
        </div>
        <StatusBadge configured={configured} />
      </header>
      <div className="px-5 py-4 space-y-3">{children}</div>
    </section>
  );
}

function TestConnectionPanel({ label, testing, result, onTest, testid }) {
  // Reusable "test connection" footer used inside each provider card.
  // Renders the button + an inline pass/fail panel when a result is set.
  // The actual API call is owned by the parent — this component is
  // purely presentational so all three channels share visual treatment.
  return (
    <div className="border-t border-slate-100 pt-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
            Test {label} connection
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Run a quick format / length check on the credentials before the next submission.
          </p>
        </div>
        <Button
          onClick={onTest}
          disabled={testing}
          variant="outline"
          data-testid={testid}
          className="h-9"
        >
          <Pulse weight="bold" className="w-4 h-4 mr-1.5" />
          {testing ? "Testing…" : "Test connection"}
        </Button>
      </div>
      {result && (
        <div
          className={`rounded-md border px-3 py-2.5 ${
            result.ok ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"
          }`}
          data-testid={`${testid}-result`}
        >
          <div className="flex items-center gap-2 mb-2">
            {result.ok ? (
              <CheckCircle weight="fill" className="w-4 h-4 text-emerald-700" />
            ) : (
              <Warning weight="fill" className="w-4 h-4 text-rose-700" />
            )}
            <span className={`text-sm font-semibold ${result.ok ? "text-emerald-900" : "text-rose-900"}`}>
              {result.ok ? `All ${label} credentials look valid` : `Some ${label} credentials failed validation`}
            </span>
          </div>
          <ul className="space-y-1">
            {(result.checks || []).map((c) => (
              <li
                key={c.name}
                className="flex items-start gap-2 text-xs"
                data-testid={`${testid}-check-${c.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              >
                {c.passed ? (
                  <CheckCircle weight="fill" className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
                ) : (
                  <XCircle weight="fill" className="w-3.5 h-3.5 text-rose-700 mt-0.5 shrink-0" />
                )}
                <span className="font-medium text-slate-800">{c.name}:</span>
                <span className="text-slate-600">{c.message}</span>
              </li>
            ))}
          </ul>
          {result.note && (
            <p className="text-[10.5px] text-slate-500 mt-2 italic">{result.note}</p>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Companies House XML Gateway (MR01) — self-contained section with
 * credential fields, current submission counter, and a "Send synthetic
 * MR01 test" button that fires against CH's sandbox and displays the
 * response inline so the solicitor can forward the submission number
 * to Ioan @ CH SDST during onboarding.
 *
 * State (status + last test result) is local to this component — the
 * parent doesn't need to know about it because the test doesn't
 * persist anything user-facing beyond the audit collection.
 */
function ChXmlGatewaySection({ data, set, configured }) {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [envelopeOpen, setEnvelopeOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data: s } = await API.get("/ch-xmlgw/status");
      setStatus(s);
    } catch {
      /* silent — endpoint returns 403 for non-solicitors */
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const runTest = async () => {
    setTesting(true);
    setLastResult(null);
    try {
      // Empty body → backend uses `_synthetic_defaults()` — safest
      // first submission for Ioan to review.
      const { data: r } = await API.post("/ch-xmlgw/test-mr01", {});
      setLastResult(r);
      if (r.status === "submitted") {
        toast.success(`MR01 accepted. CH submission #${r.submission_ref}`);
      } else if (r.status === "test_passed") {
        toast.success("MR01 passed the CH test gateway — schema validated, no errors.");
      } else {
        const first = (r.errors && r.errors[0]) || {};
        toast.error(`CH rejected: [${first.number || "?"}] ${first.text || "see envelope"}`);
      }
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <SectionCard
      icon={Buildings}
      title="Companies House — XML Gateway (MR01 charges)"
      subtitle="Separate presenter creds from the OAuth Filing API above. Used for direct XML filing of mortgage charge registrations (MR01/MR04)."
      configured={configured}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field
          label="Presenter ID"
          hint="11-char alphanumeric issued by CH SDST (Ioan) with your test/live pack."
          placeholder="e.g. 66666625000"
          value={data.ch_xmlgw_presenter_id}
          onChange={(v) => set("ch_xmlgw_presenter_id", v)}
          testid="field-ch-xmlgw-presenter-id"
        />
        <Field
          label="Authentication value"
          type="password"
          hint="Also called Presenter Password. Hashed MD5 before transmission."
          placeholder="(from CH SDST)"
          value={data.ch_xmlgw_auth_value}
          onChange={(v) => set("ch_xmlgw_auth_value", v)}
          testid="field-ch-xmlgw-auth-value"
        />
        <Field
          label="Package reference"
          hint="Sandbox pack = 0012. Live pack = 4280 (confirmed at conformance pass)."
          placeholder="Sandbox 0012 · Live 4280"
          value={data.ch_xmlgw_package_reference}
          onChange={(v) => set("ch_xmlgw_package_reference", v)}
          testid="field-ch-xmlgw-package-reference"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end pt-1">
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Environment</Label>
          <select
            value={data.ch_xmlgw_test_flag ?? "1"}
            onChange={(e) => set("ch_xmlgw_test_flag", e.target.value)}
            className="h-9 text-sm rounded-md border border-slate-200 bg-white px-3 w-full"
            data-testid="field-ch-xmlgw-test-flag"
          >
            <option value="1">Sandbox (xmltest.companieshouse.gov.uk)</option>
            <option value="0">Live (xmlgw.companieshouse.gov.uk)</option>
          </select>
          <p className="text-[10.5px] text-slate-500 leading-snug">
            Live uses pack <strong>4280</strong> and your <strong>live</strong> presenter
            credentials — your sandbox Presenter ID/Auth will <strong>not</strong> work on Live.
          </p>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500">Submissions sent</div>
          <div className="text-lg font-mono font-semibold text-slate-900" data-testid="ch-xmlgw-counter">
            {status?.submission_counter ?? "—"}
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500">Next submission #</div>
          <div className="text-lg font-mono font-semibold text-[#1462a0]" data-testid="ch-xmlgw-next">
            {status?.next_submission_number ?? "—"}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Send synthetic MR01 test
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Fires a fully-formed but fictional MR01 at the configured endpoint (Sandbox uses
              CH&apos;s test data pack — no real charge registered). Response includes the submission
              number CH assigns, plus the exact XML envelope we sent. Forward that submission number
              to Ioan when he asks for one to review.
            </p>
          </div>
          <Button
            onClick={runTest}
            disabled={testing || !configured}
            className="bg-[#1462a0] hover:bg-[#10507f] text-white h-9"
            data-testid="ch-xmlgw-test-mr01"
          >
            <Pulse weight="bold" className="w-4 h-4 mr-1.5" />
            {testing ? "Sending…" : "Send synthetic MR01"}
          </Button>
        </div>
        {!configured && (
          <p className="text-[11px] text-rose-700">Enter Presenter ID, Authentication Value and Package Reference above, then click <strong>Save credentials</strong> at the bottom of the page before running a test.</p>
        )}

        {lastResult && (
          <div
            className={`rounded-md border px-3 py-3 space-y-2 ${
              (lastResult.status === "submitted" || lastResult.status === "test_passed")
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"
            }`}
            data-testid="ch-xmlgw-test-result"
          >
            <div className="flex items-center gap-2">
              {(lastResult.status === "submitted" || lastResult.status === "test_passed") ? (
                <CheckCircle weight="fill" className="w-4 h-4 text-emerald-700" />
              ) : (
                <Warning weight="fill" className="w-4 h-4 text-rose-700" />
              )}
              <span
                className={`text-sm font-semibold ${
                  (lastResult.status === "submitted" || lastResult.status === "test_passed") ? "text-emerald-900" : "text-rose-900"
                }`}
              >
                {lastResult.status === "submitted"
                  ? `Accepted by Companies House — submission #${lastResult.submission_ref}`
                  : lastResult.status === "test_passed"
                  ? "Passed the Companies House test gateway — schema validated, no errors"
                  : "Rejected by Companies House"}
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[11px]">
              <div><span className="text-slate-500">Our submission #:</span> <span className="font-mono">{lastResult.our_submission_number}</span></div>
              <div><span className="text-slate-500">Transaction ID:</span> <span className="font-mono">{lastResult.transaction_id?.slice(0, 12)}…</span></div>
              <div className="sm:col-span-2"><span className="text-slate-500">Endpoint:</span> <span className="font-mono break-all">{lastResult.endpoint}</span></div>
            </div>
            {lastResult.errors && lastResult.errors.length > 0 && (
              <ul className="space-y-1 pt-1 border-t border-rose-100">
                {lastResult.errors.map((e, i) => (
                  <li key={i} className="text-[11px] text-rose-900">
                    <span className="font-mono font-semibold">[{e.number}]</span> {e.text}
                  </li>
                ))}
              </ul>
            )}
            <div className="pt-2 border-t border-slate-100/60">
              <button
                type="button"
                className="text-[11px] text-slate-600 hover:text-slate-900 underline"
                onClick={() => setEnvelopeOpen((o) => !o)}
                data-testid="ch-xmlgw-toggle-envelope"
              >
                {envelopeOpen ? "Hide" : "Show"} exact XML envelope + CH response
              </button>
              {envelopeOpen && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-2">
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Sent envelope</div>
                    <pre className="text-[10.5px] bg-white border border-slate-200 rounded p-2 max-h-64 overflow-auto font-mono whitespace-pre-wrap break-all" data-testid="ch-xmlgw-envelope">
                      {lastResult.envelope || "(none)"}
                    </pre>
                  </div>
                  <div>
                    <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500 mb-1">CH response</div>
                    <pre className="text-[10.5px] bg-white border border-slate-200 rounded p-2 max-h-64 overflow-auto font-mono whitespace-pre-wrap break-all" data-testid="ch-xmlgw-response">
                      {lastResult.raw_response || "(none)"}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

/**
 * HMLR Business Gateway (SOAP) — Information Services + eDRS.
 * Shows credential fields, endpoint reference table (auto-populated
 * per environment), mTLS certificate upload / removal, and the
 * reliable-messaging counter.
 *
 * There is deliberately no "test connection" button yet — the mTLS
 * certificate hasn't arrived from HMLR Security Operations, so a live
 * call would fail before we ever reach WSSE authentication. That
 * button lands once the cert is uploaded and per-service submitters
 * are wired.
 */
function HmlrBgSection({ data, set, configured }) {
  const [status, setStatus] = useState(null);
  const [certPem, setCertPem] = useState("");
  const [keyPem, setKeyPem] = useState("");
  const [uploading, setUploading] = useState(false);
  const [showCertForm, setShowCertForm] = useState(false);
  const [endpointsOpen, setEndpointsOpen] = useState(false);
  const [previewScore, setPreviewScore] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data: s } = await API.get("/hmlr-bg/status");
      setStatus(s);
    } catch {
      /* 403 for non-solicitors — silent */
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus, data.hmlr_bg_environment]);

  const uploadCert = async () => {
    if (!certPem.includes("-----BEGIN")) {
      toast.error("Paste a PEM-encoded certificate (must include the -----BEGIN header).");
      return;
    }
    setUploading(true);
    try {
      await API.post("/hmlr-bg/certificate", {
        cert_pem: certPem,
        key_pem: keyPem || null,
        environment: data.hmlr_bg_environment || undefined,
      });
      toast.success("Certificate installed");
      setCertPem("");
      setKeyPem("");
      setShowCertForm(false);
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeCert = async () => {
    if (!window.confirm("Remove the installed mTLS certificate? All HMLR calls will fail until a new one is uploaded.")) return;
    try {
      await API.delete("/hmlr-bg/certificate");
      toast.success("Certificate removed");
      loadStatus();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Remove failed");
    }
  };

  const loadPreview = async () => {
    setPreviewLoading(true);
    try {
      const { data: s } = await API.get("/hmlr-bg/title-risk-score/preview");
      setPreviewScore(s);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Preview failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const certReady = status?.certificate?.has_cert && status?.certificate?.has_key;

  return (
    <SectionCard
      icon={Buildings}
      title="HM Land Registry — Business Gateway (SOAP)"
      subtitle="Information Services (OC1, OC2, OS1/OS2, K15/K16, SIM, Register Extract, Application Enquiry) + Local Land Charges + Online Ownership Verification. Uses WSSE UsernameToken auth over mTLS."
      configured={configured}
    >
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Field
          label="User ID"
          hint="HM Land Registry portal user, e.g. BGUser001."
          placeholder="e.g. BGUser001"
          value={data.hmlr_bg_user_id}
          onChange={(v) => set("hmlr_bg_user_id", v)}
          testid="field-hmlr-bg-user-id"
        />
        <Field
          label="Password"
          type="password"
          hint="Portal password. Sent in the WSSE UsernameToken header on every SOAP call."
          placeholder="(portal password)"
          value={data.hmlr_bg_password}
          onChange={(v) => set("hmlr_bg_password", v)}
          testid="field-hmlr-bg-password"
        />
        <Field
          label="Key number"
          hint="HMLR account key (audit only) — e.g. 9587497."
          placeholder="e.g. 9587497"
          value={data.hmlr_bg_key_number}
          onChange={(v) => set("hmlr_bg_key_number", v)}
          testid="field-hmlr-bg-key-number"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end pt-1">
        <div className="space-y-1">
          <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Environment</Label>
          <select
            value={data.hmlr_bg_environment || "test"}
            onChange={(e) => set("hmlr_bg_environment", e.target.value)}
            className="h-9 text-sm rounded-md border border-slate-200 bg-white px-3 w-full"
            data-testid="field-hmlr-bg-environment"
          >
            <option value="test">Test stubs (bgtest.landregistry.gov.uk — canned responses)</option>
            <option value="live">Live (businessgateway.landregistry.gov.uk — real charges)</option>
          </select>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500">mTLS certificate</div>
          <div className="text-sm font-semibold" data-testid="hmlr-bg-cert-status">
            {certReady ? (
              <span className="text-emerald-700 flex items-center gap-1">
                <CheckCircle weight="fill" className="w-4 h-4" /> Installed
              </span>
            ) : status?.certificate?.has_cert ? (
              <span className="text-amber-700">Cert present, key missing</span>
            ) : (
              <span className="text-rose-700">Not installed</span>
            )}
          </div>
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2">
          <div className="text-[10.5px] uppercase tracking-wide font-semibold text-slate-500">Messages sent</div>
          <div className="text-lg font-mono font-semibold text-slate-900" data-testid="hmlr-bg-counter">
            {status?.message_counter ?? "—"}
          </div>
        </div>
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              mTLS client certificate
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Issued by HMLR Security Operations after the Developer Licence is countersigned.
              Upload the PEM certificate and private key (or a concatenated bundle) — files are
              stored with 0600 perms and never leave this environment.
            </p>
          </div>
          <div className="flex gap-2">
            {certReady && (
              <Button
                variant="outline"
                onClick={removeCert}
                className="text-rose-700 border-rose-300 hover:bg-rose-50 h-9"
                data-testid="hmlr-bg-cert-remove"
              >
                Remove
              </Button>
            )}
            <Button
              onClick={() => setShowCertForm((v) => !v)}
              className="bg-[#1462a0] hover:bg-[#10507f] text-white h-9"
              data-testid="hmlr-bg-cert-upload-toggle"
            >
              {certReady ? "Replace certificate" : "Upload certificate"}
            </Button>
          </div>
        </div>

        {showCertForm && (
          <div className="space-y-2 bg-slate-50 border border-slate-200 rounded p-3">
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                Certificate (PEM)
              </Label>
              <textarea
                value={certPem}
                onChange={(e) => setCertPem(e.target.value)}
                placeholder={`-----BEGIN CERTIFICATE-----\nMIIF...\n-----END CERTIFICATE-----`}
                className="w-full mt-1 h-32 text-[11px] font-mono border border-slate-200 rounded p-2 bg-white"
                data-testid="hmlr-bg-cert-pem"
              />
              <p className="text-[10.5px] text-slate-500 mt-1">
                Paste the full <code>-----BEGIN CERTIFICATE-----</code> block. If your bundle also
                contains <code>-----BEGIN PRIVATE KEY-----</code>, paste both here and leave the
                key field blank; we&apos;ll split them automatically.
              </p>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                Private key (PEM) — optional if concatenated above
              </Label>
              <textarea
                value={keyPem}
                onChange={(e) => setKeyPem(e.target.value)}
                placeholder={`-----BEGIN PRIVATE KEY-----\nMIIE...\n-----END PRIVATE KEY-----`}
                className="w-full mt-1 h-24 text-[11px] font-mono border border-slate-200 rounded p-2 bg-white"
                data-testid="hmlr-bg-key-pem"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowCertForm(false); setCertPem(""); setKeyPem(""); }}
                className="h-9"
                data-testid="hmlr-bg-cert-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={uploadCert}
                disabled={uploading || !certPem}
                className="bg-emerald-700 hover:bg-emerald-800 text-white h-9"
                data-testid="hmlr-bg-cert-save"
              >
                {uploading ? "Uploading…" : "Save certificate"}
              </Button>
            </div>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 pt-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
              Title Risk Score preview
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Runs against Cheryl&apos;s mock register (LD400530). Preview the exact case-header badge
              solicitors will see the moment the Register Extract Service is wired live. This mock
              is a genuinely high-risk title — Option to Purchase + Unilateral Notice + Equitable
              Charge on top of a Northern Bank mortgage + 3 restrictions + home rights + leasehold.
            </p>
          </div>
          <Button
            onClick={loadPreview}
            disabled={previewLoading}
            variant="outline"
            className="h-9 border-slate-300"
            data-testid="hmlr-bg-preview-risk-score"
          >
            {previewLoading ? "Scoring…" : previewScore ? "Re-run preview" : "Preview badge"}
          </Button>
        </div>
        {previewScore && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="hmlr-bg-preview-panel">
            <div className="flex items-center gap-3">
              <span className="text-[11px] text-slate-600">Case-header badge:</span>
              <TitleRiskBadge score={previewScore} showClean testid="preview-title-risk-badge" />
            </div>
            <p className="text-[10.5px] text-slate-500">
              Hover the badge to see the full factor breakdown. Same component will render on every
              case header once RES data lands on the case document.
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-slate-100 pt-4">
        <button
          type="button"
          className="text-[11px] text-slate-600 hover:text-slate-900 underline"
          onClick={() => setEndpointsOpen((v) => !v)}
          data-testid="hmlr-bg-endpoints-toggle"
        >
          {endpointsOpen ? "Hide" : "Show"} endpoint reference ({data.hmlr_bg_environment || "test"})
        </button>
        {endpointsOpen && status?.endpoints && (
          <div className="mt-2 rounded border border-slate-200 bg-white">
            <table className="w-full text-[11px]" data-testid="hmlr-bg-endpoints-table">
              <thead className="bg-slate-50 text-slate-600 uppercase text-[10px] tracking-wide">
                <tr>
                  <th className="text-left px-3 py-1.5">Service</th>
                  <th className="text-left px-3 py-1.5">Main endpoint</th>
                  <th className="text-left px-3 py-1.5">Poll endpoint</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Object.entries(status.endpoints).map(([key, urls]) => (
                  <tr key={key}>
                    <td className="px-3 py-1.5 font-mono text-slate-800">{key}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-600 break-all">{urls.main}</td>
                    <td className="px-3 py-1.5 font-mono text-slate-600 break-all">{urls.poll}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SectionCard>
  );
}

export default function SubmissionSettingsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    ch_presenter_id: "",
    ch_presenter_auth: "",
    ch_company_auth_code: "",
    ch_lender_auth_code: "",
    ch_filing_client_id: "",
    ch_filing_client_secret: "",
    ch_filing_environment: "test",
    ch_package_id: "",
    ch_company_auth_codes: {},
    hmrc_user_id: "",
    hmrc_password: "",
    hmrc_agent_reference: "",
    hmrc_vendor_id: "",
    hmlr_key_number: "",
    hmlr_account_reference: "",
    hmlr_channel_partner_id: "",
    thirdfort_api_key: "",
    thirdfort_webhook_secret: "",
    thirdfort_environment: "sandbox",
    // Companies House XML Gateway (MR01 filings) — separate creds from
    // the OAuth Filing API above (different auth mechanism entirely).
    ch_xmlgw_presenter_id: "",
    ch_xmlgw_auth_value: "",
    ch_xmlgw_package_reference: "",
    ch_xmlgw_test_flag: "1",
    // HMLR Business Gateway (SOAP) — Information Services + eDRS.
    // mTLS cert is uploaded separately via /api/hmlr-bg/certificate.
    hmlr_bg_user_id: "",
    hmlr_bg_password: "",
    hmlr_bg_key_number: "",
    hmlr_bg_environment: "test",
    smartsearch_app_id: "",
    smartsearch_secret: "",
    smartsearch_account_identifier: "",
    smartsearch_group_id: "",
    smartsearch_config_id: "",
    smartsearch_environment: "sandbox",
    // Legacy scaffold fields — hidden from UI but round-tripped so
    // previously-saved values are preserved.
    smartsearch_api_key: "",
    smartsearch_account_ref: "",
    _configured: {},
  });
  // Local-only state for the per-company auth code editor (so the user
  // can type a company number + code before committing it to the map).
  const [newCompanyNo, setNewCompanyNo] = useState("");
  const [newCompanyCode, setNewCompanyCode] = useState("");
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});
  const [chOauth, setChOauth] = useState(null);
  const [chOauthBusy, setChOauthBusy] = useState(false);
  // Legalito firm-wide external API token state. Token plaintext is
  // returned ONLY at rotation time — we keep it locally for one render
  // so the admin can copy it, then clear it on the next reload.
  const [legalitoToken, setLegalitoToken] = useState(null);
  const [legalitoTokenBusy, setLegalitoTokenBusy] = useState(false);
  const [legalitoTokenPlain, setLegalitoTokenPlain] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [letterhead, setLetterhead] = useState({ firm_name: "", firm_address: "", acting_solicitor_name: "", acting_solicitor_email: "" });
  const [letterheadSaving, setLetterheadSaving] = useState(false);

  const loadLetterhead = useCallback(async () => {
    try {
      const { data: resp } = await API.get("/firm/letterhead");
      setLetterhead(resp);
    } catch (err) { /* best-effort */ }
  }, []);

  const setLh = (k, v) => setLetterhead((s) => ({ ...s, [k]: v }));

  const handleSaveLetterhead = async () => {
    setLetterheadSaving(true);
    const tid = toast.loading("Saving firm letterhead…");
    try {
      const { data: resp } = await API.put("/firm/letterhead", letterhead);
      setLetterhead(resp);
      toast.success("Firm letterhead saved", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed", { id: tid });
    } finally {
      setLetterheadSaving(false);
    }
  };

  // ── Contract Pack defaults ──────────────────────────────────────────
  const PACK_CATEGORIES = [
    ["searches", "Searches"],
    ["property_info", "Property information forms"],
    ["proof_of_funds", "Proof of funds"],
    ["id_verification", "ID verification"],
    ["terms_of_business", "Terms of business"],
  ];
  const [packDefaults, setPackDefaults] = useState({ special_condition_keys: [], also_pdf: true, attach_categories: [], special_conditions: [] });
  const [packSaving, setPackSaving] = useState(false);

  const loadPackDefaults = useCallback(async () => {
    try {
      const { data: resp } = await API.get("/firm/contract-pack-defaults");
      setPackDefaults({
        special_condition_keys: resp.special_condition_keys || [],
        also_pdf: resp.also_pdf !== undefined ? resp.also_pdf : true,
        attach_categories: resp.attach_categories || [],
        special_conditions: resp.special_conditions || [],
      });
    } catch (err) { /* best-effort */ }
  }, []);

  const toggleInList = (list, key) => (list.includes(key) ? list.filter((k) => k !== key) : [...list, key]);

  const handleSavePackDefaults = async () => {
    setPackSaving(true);
    const tid = toast.loading("Saving Contract Pack defaults…");
    try {
      const { data: resp } = await API.put("/firm/contract-pack-defaults", {
        special_condition_keys: packDefaults.special_condition_keys,
        also_pdf: packDefaults.also_pdf,
        attach_categories: packDefaults.attach_categories,
      });
      setPackDefaults((s) => ({ ...s, ...resp }));
      toast.success("Contract Pack defaults saved", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed", { id: tid });
    } finally {
      setPackSaving(false);
    }
  };

  useEffect(() => {
    if (user === false) navigate("/login");
    else if (user && user.role !== "solicitor") {
      toast.error("Solicitor access required");
      navigate("/");
    }
  }, [user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: resp } = await API.get("/firm/submission-settings");
      setData({ ...resp, ch_company_auth_codes: resp.ch_company_auth_codes || {} });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadChOauth = useCallback(async () => {
    try {
      const { data: resp } = await API.get("/companies-house/oauth/status");
      setChOauth(resp);
    } catch (err) {
      // Status endpoint is best-effort — silently swallow so a missing
      // CH config doesn't blow up the whole page.
      setChOauth({ connected: false });
    }
  }, []);

  const loadLegalitoToken = useCallback(async () => {
    try {
      const { data: resp } = await API.get("/legalito/external/token");
      setLegalitoToken(resp);
    } catch (err) {
      setLegalitoToken({ configured: false });
    }
  }, []);

  const handleRotateLegalitoToken = async () => {
    const isRotation = legalitoToken?.configured;
    if (isRotation && !window.confirm(
      "Rotating the Legalito API token invalidates the previous one immediately. " +
      "Make sure you update Legalito's integration setup with the new token straight after. Continue?"
    )) return;
    setLegalitoTokenBusy(true);
    try {
      const { data: resp } = await API.post("/legalito/external/token/rotate");
      setLegalitoTokenPlain(resp.token);
      await loadLegalitoToken();
      toast.success(isRotation ? "New token issued" : "Token created");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Token rotation failed");
    } finally {
      setLegalitoTokenBusy(false);
    }
  };

  const handleRevokeLegalitoToken = async () => {
    if (!window.confirm("Revoke the Legalito API token? Legalito will lose access to documents on cases pushed from here until you issue a new one.")) return;
    setLegalitoTokenBusy(true);
    try {
      await API.delete("/legalito/external/token");
      setLegalitoTokenPlain("");
      await loadLegalitoToken();
      toast.success("Token revoked");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Revoke failed");
    } finally {
      setLegalitoTokenBusy(false);
    }
  };

  useEffect(() => { if (user?.role === "solicitor") { load(); loadChOauth(); loadLegalitoToken(); loadLetterhead(); loadPackDefaults(); } }, [user, load, loadChOauth, loadLegalitoToken, loadLetterhead, loadPackDefaults]);

  // Handle the OAuth callback redirect — toast result + clear the param.
  useEffect(() => {
    const flag = searchParams.get("ch_connected");
    if (!flag) return;
    if (flag === "ok") {
      toast.success("Companies House Filing API connected");
      loadChOauth();
    } else {
      const reason = searchParams.get("reason") || "unknown";
      toast.error(`Companies House connection failed: ${reason}`);
    }
    searchParams.delete("ch_connected");
    searchParams.delete("reason");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, loadChOauth]);

  const handleAuthoriseCh = async () => {
    setChOauthBusy(true);
    try {
      const { data: resp } = await API.get("/companies-house/oauth/authorize");
      window.location.href = resp.authorise_url;
    } catch (err) {
      setChOauthBusy(false);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start authorisation");
    }
  };

  const handleDisconnectCh = async () => {
    if (!window.confirm("Disconnect Companies House Filing API? You'll need to authorise again before the next MR01.")) return;
    try {
      await API.post("/companies-house/oauth/disconnect");
      toast.success("Disconnected");
      loadChOauth();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Disconnect failed");
    }
  };

  const handleRefreshCh = async () => {
    setChOauthBusy(true);
    try {
      const { data: resp } = await API.post("/companies-house/oauth/refresh");
      toast.success(`Token refreshed — expires ${new Date(resp.expires_at).toLocaleString("en-GB")}`);
      loadChOauth();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Refresh failed");
    } finally {
      setChOauthBusy(false);
    }
  };

  const handleTestChCall = async () => {
    setChOauthBusy(true);
    try {
      const { data: resp } = await API.post("/companies-house/oauth/test-call");
      if (resp.ok) {
        const who = [resp.profile_forename, resp.profile_surname].filter(Boolean).join(" ") || resp.profile_email || "your account";
        toast.success(`Live call succeeded — authenticated as ${who} on ${resp.environment} environment`);
      } else {
        toast.error(`Live call failed (HTTP ${resp.status_code}): ${resp.message}`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test call failed");
    } finally {
      setChOauthBusy(false);
    }
  };

  const set = (k, v) => setData((s) => ({ ...s, [k]: v }));

  const addCompanyCode = () => {
    const num = newCompanyNo.trim().toUpperCase();
    const code = newCompanyCode.trim();
    if (!num || !code) return toast.error("Enter both company number and auth code");
    setData((s) => ({ ...s, ch_company_auth_codes: { ...s.ch_company_auth_codes, [num]: code } }));
    setNewCompanyNo(""); setNewCompanyCode("");
  };
  const removeCompanyCode = (num) => {
    setData((s) => {
      const next = { ...s.ch_company_auth_codes };
      next[num] = ""; // empty string -> backend clears the entry
      return { ...s, ch_company_auth_codes: next };
    });
  };

  const handleTestConnection = async (channel, path, label) => {
    setTesting((s) => ({ ...s, [channel]: true }));
    setTestResults((s) => ({ ...s, [channel]: null }));
    try {
      const { data: resp } = await API.post(`/firm/submission-settings/test/${path}`);
      setTestResults((s) => ({ ...s, [channel]: resp }));
      if (resp.ok) {
        toast.success(`${label} credentials look valid`);
      } else {
        const failed = (resp.checks || []).filter((c) => !c.passed).length;
        toast.error(`${failed} ${label} check${failed === 1 ? "" : "s"} failed — see details below`);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Test failed");
    } finally {
      setTesting((s) => ({ ...s, [channel]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const tid = toast.loading("Saving credentials…");
    try {
      const { data: resp } = await API.put("/firm/submission-settings", {
        ch_presenter_id: data.ch_presenter_id,
        ch_presenter_auth: data.ch_presenter_auth,
        ch_company_auth_code: data.ch_company_auth_code,
        ch_lender_auth_code: data.ch_lender_auth_code,
        ch_filing_client_id: data.ch_filing_client_id,
        ch_filing_client_secret: data.ch_filing_client_secret,
        ch_filing_environment: data.ch_filing_environment,
        ch_package_id: data.ch_package_id,
        ch_company_auth_codes: data.ch_company_auth_codes,
        hmrc_user_id: data.hmrc_user_id,
        hmrc_password: data.hmrc_password,
        hmrc_agent_reference: data.hmrc_agent_reference,
        hmrc_vendor_id: data.hmrc_vendor_id,
        hmlr_key_number: data.hmlr_key_number,
        hmlr_account_reference: data.hmlr_account_reference,
        hmlr_channel_partner_id: data.hmlr_channel_partner_id,
        thirdfort_api_key: data.thirdfort_api_key,
        thirdfort_webhook_secret: data.thirdfort_webhook_secret,
        thirdfort_environment: data.thirdfort_environment,
        ch_xmlgw_presenter_id: data.ch_xmlgw_presenter_id,
        ch_xmlgw_auth_value: data.ch_xmlgw_auth_value,
        ch_xmlgw_package_reference: data.ch_xmlgw_package_reference,
        ch_xmlgw_test_flag: data.ch_xmlgw_test_flag,
        hmlr_bg_user_id: data.hmlr_bg_user_id,
        hmlr_bg_password: data.hmlr_bg_password,
        hmlr_bg_key_number: data.hmlr_bg_key_number,
        hmlr_bg_environment: data.hmlr_bg_environment,
        smartsearch_app_id: data.smartsearch_app_id,
        smartsearch_secret: data.smartsearch_secret,
        smartsearch_account_identifier: data.smartsearch_account_identifier,
        smartsearch_group_id: data.smartsearch_group_id,
        smartsearch_config_id: data.smartsearch_config_id,
        smartsearch_environment: data.smartsearch_environment,
      });
      setData({ ...resp, ch_company_auth_codes: resp.ch_company_auth_codes || {} });
      toast.success("Credentials saved", { id: tid });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed", { id: tid });
    } finally {
      setSaving(false);
    }
  };

  const cfg = data._configured || {};
  const companyEntries = Object.entries(data.ch_company_auth_codes || {}).filter(([, v]) => v !== "");

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="submission-settings-page">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>
            Submission Credentials
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Set the presenter / agent credentials we use to file SDLT, AP1, MR01 and AML on behalf of clients.
            Secrets are masked once saved — type new values to overwrite, or leave them as masked to keep the existing ones.
          </p>
        </div>

        {loading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <>
            {/* Firm letterhead — prefills the Contract Pack seller's-conveyancer block */}
            <SectionCard
              icon={Bank}
              title="Firm letterhead"
              subtitle="Used to prefill the Contract Pack seller's-conveyancer block and email signature"
              configured={!!(letterhead.firm_name && letterhead.acting_solicitor_email)}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Firm name</Label>
                  <Input value={letterhead.firm_name || ""} onChange={(e) => setLh("firm_name", e.target.value)} placeholder="UKC Legal Ltd" className="h-9 text-sm" data-testid="letterhead-firm-name" />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Firm address</Label>
                  <Input value={letterhead.firm_address || ""} onChange={(e) => setLh("firm_address", e.target.value)} placeholder="1 High Street, Norwich, NR1 1AA" className="h-9 text-sm" data-testid="letterhead-firm-address" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Acting solicitor name</Label>
                  <Input value={letterhead.acting_solicitor_name || ""} onChange={(e) => setLh("acting_solicitor_name", e.target.value)} placeholder="Steve Smith" className="h-9 text-sm" data-testid="letterhead-solicitor-name" />
                </div>
                <div className="space-y-1">
                  <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Acting solicitor email</Label>
                  <Input value={letterhead.acting_solicitor_email || ""} onChange={(e) => setLh("acting_solicitor_email", e.target.value)} placeholder="steve@ukclegal.co.uk" className="h-9 text-sm" data-testid="letterhead-solicitor-email" />
                </div>
              </div>
              <div className="flex justify-end pt-1">
                <Button onClick={handleSaveLetterhead} disabled={letterheadSaving} className="h-9 bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="letterhead-save">
                  <FloppyDisk weight="bold" className="w-4 h-4 mr-1.5" /> {letterheadSaving ? "Saving…" : "Save letterhead"}
                </Button>
              </div>
            </SectionCard>

            {/* Contract Pack defaults — pre-ticked conditions + attachments on every new pack */}
            <SectionCard
              icon={Stamp}
              title="Contract Pack defaults"
              subtitle="Special conditions + attachments every new Contract Pack starts with"
              configured={!!(packDefaults.special_condition_keys?.length || packDefaults.attach_categories?.length)}
            >
              <div>
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Pre-ticked special conditions</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {(packDefaults.special_conditions || []).map((c) => (
                    <label key={c.key} className="flex items-start gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`pack-default-cond-${c.key}`}>
                      <Checkbox
                        checked={packDefaults.special_condition_keys.includes(c.key)}
                        onCheckedChange={() => setPackDefaults((s) => ({ ...s, special_condition_keys: toggleInList(s.special_condition_keys, c.key) }))}
                      />
                      <span>{c.label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="pt-2">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">Auto-attach documents by type (when present on the case)</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {PACK_CATEGORIES.map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`pack-default-cat-${key}`}>
                      <Checkbox
                        checked={packDefaults.attach_categories.includes(key)}
                        onCheckedChange={() => setPackDefaults((s) => ({ ...s, attach_categories: toggleInList(s.attach_categories, key) }))}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer pt-2" data-testid="pack-default-pdf">
                <Checkbox
                  checked={!!packDefaults.also_pdf}
                  onCheckedChange={(v) => setPackDefaults((s) => ({ ...s, also_pdf: !!v }))}
                />
                <span>Attach a locked PDF alongside the DOCX by default</span>
              </label>
              <div className="flex justify-end pt-1">
                <Button onClick={handleSavePackDefaults} disabled={packSaving} className="h-9 bg-[#1462a0] hover:bg-[#10507f] text-white" data-testid="pack-defaults-save">
                  <FloppyDisk weight="bold" className="w-4 h-4 mr-1.5" /> {packSaving ? "Saving…" : "Save defaults"}
                </Button>
              </div>
            </SectionCard>

            {/* Companies House */}
            <SectionCard
              icon={Buildings}
              title="Companies House"
              subtitle="Presenter credentials for MR01 charge registration via WebFiling / Filing API"
              configured={cfg.companies_house}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="Presenter ID"
                  hint="8-character Companies House presenter account ID"
                  placeholder="e.g. ABC12345"
                  value={data.ch_presenter_id}
                  onChange={(v) => set("ch_presenter_id", v)}
                  testid="field-ch-presenter-id"
                />
                <Field
                  label="Presenter Authentication Code"
                  hint="Account password used to authenticate the presenter"
                  type="password"
                  placeholder="(set once and forget)"
                  value={data.ch_presenter_auth}
                  onChange={(v) => set("ch_presenter_auth", v)}
                  testid="field-ch-presenter-auth"
                />
                <Field
                  label="Company Authentication Code"
                  hint="Firm-level Company Authentication Code used when filing on behalf of corporate clients"
                  type="password"
                  placeholder="6-character code"
                  value={data.ch_company_auth_code}
                  onChange={(v) => set("ch_company_auth_code", v)}
                  testid="field-ch-company-auth-code"
                />
                <Field
                  label="Lender Authentication Code"
                  hint="Firm-level CH credential for filing mortgage / charge documents on behalf of other companies — used as the electronic signature when you don't have access to a company's own authentication code."
                  type="password"
                  placeholder="Lender auth code"
                  value={data.ch_lender_auth_code}
                  onChange={(v) => set("ch_lender_auth_code", v)}
                  testid="field-ch-lender-auth-code"
                />
              </div>

              {/* Filing API OAuth credentials (separate from WebFiling creds) */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    Filing API (OAuth 2.0)
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Credentials from your CH developer account at
                    {" "}
                    <a href="https://developer.company-information.service.gov.uk/" target="_blank" rel="noreferrer" className="text-[#1462a0] underline">
                      developer.company-information.service.gov.uk
                    </a>
                    . Used for direct MR01 / AP01 / CS01 submissions. Separate from the WebFiling credentials above.
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field
                    label="Client ID"
                    hint="UUID issued when you registered the Web application on the CH developer portal"
                    placeholder="e.g. 7def345f-08d1-4639-8429-..."
                    value={data.ch_filing_client_id}
                    onChange={(v) => set("ch_filing_client_id", v)}
                    testid="field-ch-filing-client-id"
                  />
                  <Field
                    label="Client Secret"
                    hint="Shown ONCE on the CH portal — paste it here immediately"
                    type="password"
                    placeholder="(opaque token)"
                    value={data.ch_filing_client_secret}
                    onChange={(v) => set("ch_filing_client_secret", v)}
                    testid="field-ch-filing-client-secret"
                  />
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold text-slate-700 mb-1.5 block">Environment</label>
                    <div className="flex gap-2" data-testid="field-ch-filing-environment">
                      {["test", "live"].map((env) => {
                        const active = data.ch_filing_environment === env;
                        return (
                          <button
                            key={env}
                            type="button"
                            onClick={() => set("ch_filing_environment", env)}
                            className={`px-3 py-1.5 rounded-full border text-xs font-medium transition ${
                              active
                                ? "bg-[#1462a0] text-white border-[#1462a0]"
                                : "bg-white text-slate-700 border-slate-300 hover:border-slate-400"
                            }`}
                            data-testid={`ch-filing-env-${env}`}
                          >
                            {env === "test" ? "Test (Sandbox)" : "Live (Production)"}
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Start with <strong>Test</strong> while CH approves your application. Switch to Live once they&apos;ve granted production access.
                    </p>
                  </div>
                  <Field
                    label="Package ID"
                    hint="Issued by Companies House after your software passes Filing API conformance testing. Leave blank until granted."
                    placeholder="(awaiting CH approval)"
                    value={data.ch_package_id}
                    onChange={(v) => set("ch_package_id", v)}
                    testid="field-ch-package-id"
                  />
                </div>

                {/* OAuth Authorise / Disconnect */}
                <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-3 mt-3" data-testid="ch-oauth-panel">
                  {chOauth?.connected ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <PlugsConnected weight="fill" className="w-4 h-4 text-emerald-700" />
                        <span className="text-sm font-semibold text-emerald-900">Connected to Companies House</span>
                        {chOauth.expired && (
                          <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">
                            Token expired — refresh
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-600 space-y-0.5">
                        {chOauth.connected_at && (
                          <div>Authorised: {new Date(chOauth.connected_at).toLocaleString("en-GB")}</div>
                        )}
                        {chOauth.expires_at && (
                          <div>Access token expires: {new Date(chOauth.expires_at).toLocaleString("en-GB")}</div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2 pt-1">
                        {chOauth.has_refresh_token && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleRefreshCh}
                            disabled={chOauthBusy}
                            data-testid="ch-oauth-refresh"
                          >
                            <ArrowsClockwise weight="bold" className="w-3.5 h-3.5 mr-1" />
                            Refresh token
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleTestChCall}
                          disabled={chOauthBusy}
                          data-testid="ch-oauth-test-call"
                        >
                          <Pulse weight="bold" className="w-3.5 h-3.5 mr-1" />
                          Test live call
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleDisconnectCh}
                          data-testid="ch-oauth-disconnect"
                          className="text-rose-700 hover:bg-rose-50"
                        >
                          <Trash weight="bold" className="w-3.5 h-3.5 mr-1" />
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="text-xs text-slate-600">
                        Not authorised yet. Click the button to sign in to Companies House and grant access.
                      </div>
                      <Button
                        onClick={handleAuthoriseCh}
                        disabled={chOauthBusy || !data.ch_filing_client_id || !data.ch_filing_client_secret}
                        className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                        data-testid="ch-oauth-authorise"
                      >
                        <LinkIcon weight="bold" className="w-4 h-4 mr-1.5" />
                        {chOauthBusy ? "Starting…" : "Authorise with Companies House"}
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Per-company auth codes — one per chargor */}
              <div className="border-t border-slate-100 pt-4 space-y-2">
                <div>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-700">
                    Per-company Authentication Codes
                  </h3>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    When you file MR01 for a chargor company, Companies House needs that company&apos;s own 6-character
                    authentication code. Store the codes here so they auto-fill on the MR01 form — or capture them on the
                    client&apos;s party record case-by-case.
                  </p>
                </div>

                {companyEntries.length > 0 && (
                  <ul className="space-y-1" data-testid="ch-company-codes-list">
                    {companyEntries.map(([num, code]) => (
                      <li key={num} className="flex items-center gap-2 text-sm">
                        <span className="font-mono text-slate-800 w-28">{num}</span>
                        <span className="font-mono text-slate-500 flex-1">{code}</span>
                        <Button size="sm" variant="ghost" onClick={() => removeCompanyCode(num)} className="text-rose-600 h-7 px-2" data-testid={`ch-company-code-remove-${num}`}>
                          <Trash weight="bold" className="w-3.5 h-3.5" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
                  <div className="sm:col-span-2">
                    <Field
                      label="Company number"
                      placeholder="e.g. 12345678"
                      value={newCompanyNo}
                      onChange={setNewCompanyNo}
                      testid="field-new-company-no"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <Field
                      label="Auth code"
                      placeholder="6-char code"
                      value={newCompanyCode}
                      onChange={setNewCompanyCode}
                      testid="field-new-company-code"
                    />
                  </div>
                  <Button onClick={addCompanyCode} variant="outline" className="border-dashed h-9" data-testid="add-company-code">
                    <Plus weight="bold" className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Test connection */}
              <TestConnectionPanel
                label="Companies House"
                testing={testing.companies_house}
                result={testResults.companies_house}
                onTest={() => handleTestConnection("companies_house", "companies-house", "Companies House")}
                testid="ch-test-connection"
              />
            </SectionCard>

            {/* Companies House XML Gateway — MR01 charge registrations */}
            <ChXmlGatewaySection
              data={data}
              set={set}
              configured={cfg.ch_xmlgw}
            />

            {/* HM Land Registry Business Gateway — SOAP Information Services */}
            <HmlrBgSection
              data={data}
              set={set}
              configured={cfg.hmlr_bg}
            />

            {/* HMRC SDLT */}
            <SectionCard
              icon={Stamp}
              title="HMRC — Stamp Taxes Online (SDLT)"
              subtitle="Government Gateway credentials for SDLT5 returns"
              configured={cfg.hmrc_sdlt}
            >
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field
                  label="Gateway User ID"
                  placeholder="HMRC user ID"
                  value={data.hmrc_user_id}
                  onChange={(v) => set("hmrc_user_id", v)}
                  testid="field-hmrc-user-id"
                />
                <Field
                  label="Gateway Password"
                  type="password"
                  placeholder="(set once)"
                  value={data.hmrc_password}
                  onChange={(v) => set("hmrc_password", v)}
                  testid="field-hmrc-password"
                />
                <Field
                  label="Agent reference"
                  hint="Optional — your firm reference at HMRC"
                  value={data.hmrc_agent_reference}
                  onChange={(v) => set("hmrc_agent_reference", v)}
                  testid="field-hmrc-agent-ref"
                />
                <Field
                  label="Vendor ID"
                  hint="Issued by HMRC after your software passes SDLT recognition (test scenarios + TPVS submission). Leave blank until granted."
                  placeholder="(awaiting HMRC recognition)"
                  value={data.hmrc_vendor_id}
                  onChange={(v) => set("hmrc_vendor_id", v)}
                  testid="field-hmrc-vendor-id"
                />
              </div>
              <TestConnectionPanel
                label="HMRC"
                testing={testing.hmrc_sdlt}
                result={testResults.hmrc_sdlt}
                onTest={() => handleTestConnection("hmrc_sdlt", "hmrc-sdlt", "HMRC")}
                testid="hmrc-test-connection"
              />
            </SectionCard>

            {/* HMLR */}
            <SectionCard
              icon={Bank}
              title="HM Land Registry"
              subtitle="Variable Direct Debit key for AP1 fee settlement"
              configured={cfg.hmlr}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="HMLR Key Number"
                  hint="Your Variable Direct Debit account number"
                  placeholder="e.g. 12345"
                  value={data.hmlr_key_number}
                  onChange={(v) => set("hmlr_key_number", v)}
                  testid="field-hmlr-key-number"
                />
                <Field
                  label="Account reference"
                  hint="Optional cost-centre or department code"
                  value={data.hmlr_account_reference}
                  onChange={(v) => set("hmlr_account_reference", v)}
                  testid="field-hmlr-account-ref"
                />
                <Field
                  label="Channel Partner ID"
                  hint="Issued by HMLR after Business Gateway onboarding (MoU + TLS cert handshake + conformance pack). Leave blank until granted."
                  placeholder="(awaiting HMLR onboarding)"
                  value={data.hmlr_channel_partner_id}
                  onChange={(v) => set("hmlr_channel_partner_id", v)}
                  testid="field-hmlr-channel-partner-id"
                />
              </div>
              <TestConnectionPanel
                label="HMLR"
                testing={testing.hmlr}
                result={testResults.hmlr}
                onTest={() => handleTestConnection("hmlr", "hmlr", "HMLR")}
                testid="hmlr-test-connection"
              />
            </SectionCard>

            {/* Thirdfort */}
            <SectionCard
              icon={ShieldCheck}
              title="Thirdfort — AML / ID-V"
              subtitle="API key + webhook secret used to order ID checks and receive completion callbacks"
              configured={cfg.thirdfort}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="API key"
                  type="password"
                  placeholder="(set once)"
                  value={data.thirdfort_api_key}
                  onChange={(v) => set("thirdfort_api_key", v)}
                  testid="field-thirdfort-api-key"
                />
                <Field
                  label="Webhook signing secret"
                  hint="Generated alongside the API key in Thirdfort's dashboard. Used to verify incoming case.* events."
                  type="password"
                  placeholder="(paste alongside API key)"
                  value={data.thirdfort_webhook_secret}
                  onChange={(v) => set("thirdfort_webhook_secret", v)}
                  testid="field-thirdfort-webhook-secret"
                />
              </div>
              <div className="space-y-1 mt-3">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                  Environment
                </Label>
                <select
                  value={data.thirdfort_environment || "sandbox"}
                  onChange={(e) => set("thirdfort_environment", e.target.value)}
                  className="h-9 text-sm rounded-md border border-slate-200 bg-white px-3 w-full sm:w-auto"
                  data-testid="field-thirdfort-environment"
                >
                  <option value="sandbox">Sandbox (test orders)</option>
                  <option value="live">Live (real ID-V charges apply)</option>
                </select>
                <p className="text-[11px] text-slate-500">
                  Switch to <strong>live</strong> only once Thirdfort have confirmed your plan is upgraded. Sandbox checks are free; live checks are billed per check.
                </p>
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1">
                  Webhook URL to whitelist in Thirdfort
                </div>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono text-slate-800 flex-1 break-all" data-testid="thirdfort-webhook-url">
                    {`${window.location.origin}/api/thirdfort/webhook`}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/api/thirdfort/webhook`);
                      toast.success("Webhook URL copied");
                    }}
                    data-testid="thirdfort-webhook-copy"
                  >
                    Copy
                  </Button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1.5">
                  In the Thirdfort dashboard → Integrations → add this URL and subscribe to <code>case.completed</code>, <code>case.failed</code>, <code>case.expired</code>.
                </p>
              </div>
            </SectionCard>

            {/* SmartSearch — alternative AML/ID-V provider (cheaper at scale) */}
            <SectionCard
              icon={ShieldCheck}
              title="SmartSearch — AML / ID-V (alternative to Thirdfort)"
              subtitle="JSON:API v3. Used by ~1 in 3 of the UK's top-200 law firms. Pay-per-check or tiered subscription — typically lower per-check than Thirdfort at scale."
              configured={cfg.smartsearch}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field
                  label="App ID"
                  hint="UUID issued by SmartSearch onboarding (Client Services)."
                  placeholder="e.g. a5b4d0f4-5f32-493c-8d07-b4dc41747a37"
                  value={data.smartsearch_app_id}
                  onChange={(v) => set("smartsearch_app_id", v)}
                  testid="field-smartsearch-app-id"
                />
                <Field
                  label="Secret"
                  type="password"
                  hint="Treat like a password. Rotate via SmartSearch portal if leaked."
                  placeholder="(shared secret)"
                  value={data.smartsearch_secret}
                  onChange={(v) => set("smartsearch_secret", v)}
                  testid="field-smartsearch-secret"
                />
                <Field
                  label="Account identifier"
                  hint="Optional — sub-account email or UUID to scope the token to a team. Leave blank for the top-level firm account."
                  placeholder="e.g. team@firm.co.uk"
                  value={data.smartsearch_account_identifier}
                  onChange={(v) => set("smartsearch_account_identifier", v)}
                  testid="field-smartsearch-account-identifier"
                />
                <Field
                  label="Group ID"
                  hint="Parent group UUID that owns the searches (required)."
                  placeholder="e.g. e6795c3a-5b38-4036-ab43-8200ba025f6e"
                  value={data.smartsearch_group_id}
                  onChange={(v) => set("smartsearch_group_id", v)}
                  testid="field-smartsearch-group-id"
                />
                <Field
                  label="Search config ID"
                  hint="Watchlist / rule config UUID (required for real searches)."
                  placeholder="e.g. 9d679489-ecbf-4df8-a817-2c494fc1f51e"
                  value={data.smartsearch_config_id}
                  onChange={(v) => set("smartsearch_config_id", v)}
                  testid="field-smartsearch-config-id"
                />
              </div>
              <div className="space-y-1 mt-3">
                <Label className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
                  Environment
                </Label>
                <select
                  value={data.smartsearch_environment || "sandbox"}
                  onChange={(e) => set("smartsearch_environment", e.target.value)}
                  className="h-9 text-sm rounded-md border border-slate-200 bg-white px-3 w-full sm:w-auto"
                  data-testid="field-smartsearch-environment"
                >
                  <option value="sandbox">Sandbox (test checks, free — demo data pack only)</option>
                  <option value="live">Live (real checks billed per request)</option>
                </select>
                <p className="text-[11px] text-slate-500">
                  Once both providers are configured you can pick which one runs on each case from the AML tab. Existing checks always keep the provider they were placed with.
                </p>
              </div>
            </SectionCard>

            {/* Legalito — firm-wide external document-picker token */}
            <SectionCard
              icon={LinkIcon}
              title="Legalito — External document picker token"
              subtitle="Firm-wide API token Legalito uses to pull documents from pushed matters back into their enquiry attachments"
              configured={!!legalitoToken?.configured}
            >
              {!legalitoToken?.configured ? (
                <div className="space-y-3">
                  <p className="text-sm text-slate-700">
                    Generate a long-lived API token and paste it into Legalito&apos;s integration setup. This is shared by every solicitor — per-matter access is granted automatically once a case is &quot;pushed&quot; to Legalito.
                  </p>
                  <Button
                    onClick={handleRotateLegalitoToken}
                    disabled={legalitoTokenBusy}
                    className="bg-[#1462a0] hover:bg-[#10507f] text-white"
                    data-testid="legalito-token-generate"
                  >
                    <PlugsConnected weight="bold" className="w-4 h-4 mr-1.5" />
                    {legalitoTokenBusy ? "Generating…" : "Generate token"}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5 text-xs space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-600">Token prefix</span>
                      <code className="font-mono text-slate-800" data-testid="legalito-token-prefix">
                        {legalitoToken.prefix}…{legalitoToken.last4}
                      </code>
                    </div>
                    {legalitoToken.created_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-slate-600">Created</span>
                        <span className="text-slate-800">
                          {new Date(legalitoToken.created_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                        </span>
                      </div>
                    )}
                  </div>
                  {legalitoTokenPlain && (
                    <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2.5">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-900 mb-1">
                        Copy this token now — it will not be shown again
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-white border border-amber-200 rounded px-2 py-1 font-mono text-slate-800 flex-1 break-all" data-testid="legalito-token-plain">
                          {legalitoTokenPlain}
                        </code>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(legalitoTokenPlain);
                            toast.success("Token copied");
                          }}
                          data-testid="legalito-token-copy"
                        >
                          Copy
                        </Button>
                      </div>
                      <p className="text-[11px] text-amber-800 mt-1.5">
                        Paste this into Legalito&apos;s integration setup as your firm&apos;s API token.
                      </p>
                    </div>
                  )}
                  <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2.5">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-600 mb-1">
                      Document picker base URL
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1 font-mono text-slate-800 flex-1 break-all" data-testid="legalito-picker-base-url">
                        {`${window.location.origin}/api/legalito/external/matters/{external_ref}/documents`}
                      </code>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          navigator.clipboard.writeText(`${window.location.origin}/api/legalito/external/matters/{external_ref}/documents`);
                          toast.success("URL copied");
                        }}
                        data-testid="legalito-picker-url-copy"
                      >
                        Copy
                      </Button>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1.5">
                      Authenticated via the <code>X-Legalito-Token</code> header. Provide both the URL and the token to Legalito&apos;s integrations team.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleRotateLegalitoToken}
                      disabled={legalitoTokenBusy}
                      data-testid="legalito-token-rotate"
                    >
                      <ArrowsClockwise weight="bold" className="w-4 h-4 mr-1.5" />
                      {legalitoTokenBusy ? "Rotating…" : "Rotate token"}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={handleRevokeLegalitoToken}
                      disabled={legalitoTokenBusy}
                      className="text-rose-600 hover:bg-rose-50"
                      data-testid="legalito-token-revoke"
                    >
                      Revoke
                    </Button>
                  </div>
                </div>
              )}
            </SectionCard>

            <div className="sticky bottom-3 flex justify-end">
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-[#1462a0] hover:bg-[#10507f] text-white shadow-lg"
                data-testid="submission-settings-save"
              >
                <FloppyDisk weight="bold" className="w-4 h-4 mr-1.5" />
                {saving ? "Saving…" : "Save all credentials"}
              </Button>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
